/**
 * ImageViewerModal - Displays images in a modal with parallel chat window
 * 
 * This module creates a full-screen modal that displays keyword-based images
 * alongside a parallel chat interface, keeping users in the same app tab.
 */

export class ImageViewerModal {
    constructor() {
        this.modal = null;
        this.currentImageUrl = null;
        this.currentImageTitle = null;
        this.chatController = null;
        this.initialize();
    }

    initialize() {
        console.log("ImageViewerModal: Initializing...");
        this.createModal();
        this.setupEventListeners();
        console.log("ImageViewerModal: Initialized successfully");
    }

    /**
     * Create the modal HTML structure
     */
    createModal() {
        const modalHTML = `
            <div id="image-viewer-modal" class="image-viewer-modal d-none">
                <div class="image-viewer-overlay" id="image-viewer-overlay">
                    <div class="image-viewer-container">
                        <!-- Left side - Image display -->
                        <div class="image-viewer-left">
                            <div class="image-viewer-header">
                                <button id="close-image-viewer" class="btn btn-sm btn-outline-light">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                            <div class="image-viewer-content">
                                <img id="image-viewer-image" src="" alt="Keyword Image" class="image-viewer-img">
                            </div>
                        </div>

                        <!-- Right side - Chat display only -->
                        <div class="image-viewer-right">
                            <div class="image-viewer-chat-container">
                                <div id="image-viewer-chat-history" class="image-viewer-chat-history">
                                    <!-- Chat messages will be mirrored here -->
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add the modal to the body
        document.body.insertAdjacentHTML('beforeend', modalHTML);
        this.modal = document.getElementById('image-viewer-modal');
    }

    /**
     * Setup event listeners for the modal
     */
    setupEventListeners() {
        // Close modal events
        const closeBtn = document.getElementById('close-image-viewer');
        const overlay = document.getElementById('image-viewer-overlay');
        
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeModal());
        }

        if (overlay) {
            overlay.addEventListener('click', (e) => {
                // Don't close on overlay click for compact preview
                // Only close via close button or escape key
            });
        }

        // Keyboard events
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen()) {
                this.closeModal();
            }
        });


    }

    /**
     * Open the modal with an image
     * @param {string} imageUrl - URL of the image to display
     * @param {string} title - Title/description of the image
     * @param {string} keyword - The keyword that triggered this image
     * @param {boolean} imageOnly - Whether to show only the image (hide chat)
     */
    openModal(imageUrl, title = '', keyword = '', imageOnly = true) {
        if (!this.modal) {
            console.error('ImageViewerModal: Modal not initialized');
            return;
        }

        // Stop any active slideshow when user manually opens an image
        if (window.simpleImagePanel && window.simpleImagePanel.isSlideshowActive()) {
            console.log('ImageViewerModal: Stopping slideshow due to manual image open');
            window.simpleImagePanel.stopSlideshow();
            window.simpleImagePanel.hideImage();
        }

        this.currentImageUrl = imageUrl;
        this.currentImageTitle = title || 'Keyword Image';

        // Set image
        const img = document.getElementById('image-viewer-image');
        if (img) {
            img.src = imageUrl;
            img.alt = this.currentImageTitle;
        }

        // Handle image-only mode
        if (imageOnly) {
            this.modal.classList.add('image-only');
        } else {
            this.modal.classList.remove('image-only');
            // Only mirror chat history if not in image-only mode
            this.mirrorChatHistory();
        }

        // Show modal
        this.modal.classList.remove('d-none');
        document.body.style.overflow = 'hidden'; // Prevent background scrolling

        console.log('ImageViewerModal: Modal opened with image:', imageUrl, imageOnly ? '(image only)' : '(with chat)');
    }

    /**
     * Close the modal
     */
    closeModal() {
        if (!this.modal) return;

        this.modal.classList.add('d-none');
        this.modal.classList.remove('image-only'); // Clean up image-only mode
        document.body.style.overflow = ''; // Restore scrolling
        
        // Clear current image data
        this.currentImageUrl = null;
        this.currentImageTitle = null;

        console.log('ImageViewerModal: Modal closed');
    }

    /**
     * Check if modal is currently open
     */
    isOpen() {
        return this.modal && !this.modal.classList.contains('d-none');
    }

    /**
     * Mirror the main chat history in the modal
     */
    mirrorChatHistory() {
        const mainChatHistory = document.getElementById('chat-history');
        const modalChatHistory = document.getElementById('image-viewer-chat-history');

        if (!mainChatHistory || !modalChatHistory) {
            console.warn('ImageViewerModal: Could not find chat history elements');
            return;
        }

        // Clone the main chat history content
        modalChatHistory.innerHTML = mainChatHistory.innerHTML;

        // Scroll to bottom
        modalChatHistory.scrollTop = modalChatHistory.scrollHeight;
    }



    /**
     * Set the chat controller reference
     * @param {Object} chatController - Reference to the main chat controller
     */
    setChatController(chatController) {
        this.chatController = chatController;
    }

    /**
     * Update the mirrored chat when new messages arrive
     */
    updateMirroredChat() {
        if (this.isOpen()) {
            this.mirrorChatHistory();
        }
    }
}

// Make the class available globally
window.ImageViewerModal = ImageViewerModal;